import React, { useState, useEffect, useCallback } from 'react';
import { toStringArray } from '../../utils/toStringArray';
import { Typography, Avatar } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import FlagIcon from '@mui/icons-material/Flag';
import './DoctorBooking.css';
import { ProfessionalService } from '../../services/ProfessionalService';
import PaymentService from '../../services/PaymentService';
import PaymentSuccessDialog from './PaymentSuccessDialog';
import { checkImageURL } from '../../utils/validations';
import { getImageSrc } from '../../utils/imageUtils';
import image_not_found from '../../assets/image_not_found.png';
import { UserProfilePicture } from '../../services/UserProfilePicture';
import PhoneNumberField from '../../utils/PhoneNumberField';
import { AuthService } from '../../services/AuthService';
// TeleconsultationService removed - video consultation is now created automatically by backend webhook after payment

// Using emoji flags for better compatibility and visual appeal

// UI Components
interface InputFieldProps {
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: 'text' | 'email' | 'password' | 'tel' | 'date';
  required?: boolean;
  disabled?: boolean;
  className?: string;
  name?: string;
  id?: string;
}

const InputField: React.FC<InputFieldProps> = ({
  placeholder,
  value,
  onChange,
  type = 'text',
  required = false,
  disabled = false,
  className = '',
  name,
  id
}) => {
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      required={required}
      disabled={disabled}
      name={name}
      id={id}
      className={`doctor-booking-input ${className}`}
    />
  );
};

interface TextareaProps {
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  rows?: number;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  name?: string;
  id?: string;
}

const Textarea: React.FC<TextareaProps> = ({
  placeholder,
  value,
  onChange,
  rows = 4,
  required = false,
  disabled = false,
  className = '',
  name,
  id
}) => {
  return (
    <textarea
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      rows={rows}
      required={required}
      disabled={disabled}
      name={name}
      id={id}
      className={`doctor-booking-textarea ${className}`}
    />
  );
};

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  options: DropdownOption[];
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  name?: string;
  id?: string;
  disabled?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  placeholder = 'Select an option',
  value,
  onChange,
  className = '',
  name,
  id,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || '');

  // Update selectedValue when value prop changes
  useEffect(() => {
    setSelectedValue(value || '');
  }, [value]);

  const handleSelect = (optionValue: string) => {
    if (disabled) return;
    setSelectedValue(optionValue);
    setIsOpen(false);
    if (onChange) {
      onChange(optionValue);
    }
  };

  const selectedOption = options.find(option => option.value === selectedValue);

  return (
    <div className={`doctor-booking-dropdown ${className} ${disabled ? 'disabled' : ''}`}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className="doctor-booking-dropdown-button"
        name={name}
        id={id}
        disabled={disabled}
      >
        <span>{selectedOption ? selectedOption.label : placeholder}</span>
      </button>

      {isOpen && !disabled && (
        <div className="doctor-booking-dropdown-menu">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className="doctor-booking-dropdown-item"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Component
interface Professional {
  id: number;
  name: string;
  specialty: string;
  description: string;
  image: string;
  languages: string[];
  location: string;
  price: string;
  specialties: string[];
  availability: {
    dates: number[];
    timeSlots: string[];
  };
  utilisateur_id?: number;
  honoraire_montant?: number;
  honoraire_devise?: string;
}

interface ConsultationFormData {
  name: string;
  email: string;
  country: string;
  phone: string;
  subject: string;
}

interface TimeSlot {
  time: string;
  available: boolean;
}

interface DateOption {
  day: string;
  date: number;
  month: number;
  year: number;
  isSelected?: boolean;
  isCurrentMonth?: boolean;
  isPastDate?: boolean;
  isAvailable?: boolean;
}

const countryOptions = [
  { value: 'tunisia', label: 'Tunisia', flag: '🇹🇳' },
  { value: 'france', label: 'France', flag: '🇫🇷' },
  { value: 'algeria', label: 'Algeria', flag: '🇩🇿' },
  { value: 'morocco', label: 'Morocco', flag: '🇲🇦' },
  { value: 'libya', label: 'Libya', flag: '🇱🇾' },
  { value: 'egypt', label: 'Egypt', flag: '🇪🇬' },
  { value: 'germany', label: 'Germany', flag: '🇩🇪' },
  { value: 'italy', label: 'Italy', flag: '🇮🇹' },
  { value: 'spain', label: 'Spain', flag: '🇪🇸' },
  { value: 'united kingdom', label: 'United Kingdom', flag: '🇬🇧' },
  { value: 'united states', label: 'United States', flag: '🇺🇸' },
  { value: 'canada', label: 'Canada', flag: '🇨🇦' },
  { value: 'other', label: 'Other', flag: '🌍' },
];

// Helper function to normalize country values
const normalizeCountry = (country: string): string => {
  if (!country) return 'tunisia';
  
  const normalized = country.toLowerCase().trim();
  
  // Map common variations to our dropdown values
  const countryMap: { [key: string]: string } = {
    'tunisie': 'tunisia',
    'tunisia': 'tunisia',
    'Tunisia': 'tunisia', // Handle capital case
    'TUNISIA': 'tunisia', // Handle all caps
    'france': 'france',
    'France': 'france',
    'algerie': 'algeria',
    'algeria': 'algeria',
    'Algeria': 'algeria',
    'maroc': 'morocco',
    'morocco': 'morocco',
    'Morocco': 'morocco',
    'libye': 'libya',
    'libya': 'libya',
    'Libya': 'libya',
    'egypte': 'egypt',
    'egypt': 'egypt',
    'Egypt': 'egypt',
    'allemagne': 'germany',
    'germany': 'germany',
    'Germany': 'germany',
    'italie': 'italy',
    'italy': 'italy',
    'Italy': 'italy',
    'espagne': 'spain',
    'spain': 'spain',
    'Spain': 'spain',
    'royaume-uni': 'united kingdom',
    'united kingdom': 'united kingdom',
    'United Kingdom': 'united kingdom',
    'uk': 'united kingdom',
    'UK': 'united kingdom',
    'etats-unis': 'united states',
    'united states': 'united states',
    'United States': 'united states',
    'usa': 'united states',
    'USA': 'united states',
    'canada': 'canada',
    'Canada': 'canada',
  };
  
  return countryMap[normalized] || 'tunisia';
};

interface FormData {
  name: string;
  email: string;
  country: string;
  phone: string;
  birthdate: string;
  subject: string;
  address: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  country?: string;
  phone?: string;
  birthdate?: string;
  subject?: string;
  address?: string;
}

const DoctorBooking: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [professional] = useState<Professional | undefined>(() => {
    const normalize = (raw: Professional | undefined): Professional | undefined => {
      if (!raw) return undefined;
      return {
        ...raw,
        specialties: toStringArray(raw.specialties),
        languages: toStringArray(raw.languages),
      };
    };
    const fromNav = location.state?.professional as Professional | undefined;
    if (fromNav) {
      localStorage.removeItem('pendingDoctorBooking');
      return normalize(fromNav);
    }
    try {
      const raw = localStorage.getItem('pendingDoctorBooking');
      if (!raw) return undefined;
      const parsed = JSON.parse(raw);
      localStorage.removeItem('pendingDoctorBooking');
      return normalize(parsed?.professional as Professional | undefined);
    } catch {
      localStorage.removeItem('pendingDoctorBooking');
      return undefined;
    }
  });
  
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    country: 'tunisia', // Set default value
    phone: '',
    birthdate: '',
    subject: '',
    address: ''
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isLoadingUserData, setIsLoadingUserData] = useState<boolean>(false);
  const [disponibilites, setDisponibilites] = useState<any[]>([]); // Raw disponibilites from backend
  const [consultations, setConsultations] = useState<any[]>([]); // Raw consultations (taken rdv)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [paymentFinalized, setPaymentFinalized] = useState<boolean>(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
  const [consultationIdForDialog, setConsultationIdForDialog] = useState<number | null>(null);
  const [videoStatusForDialog, setVideoStatusForDialog] = useState<string>('pending');
  const [allTimeSlots, setAllTimeSlots] = useState<string[]>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  // isCreatingVideoConsultation removed - backend handles this now

  useEffect(() => {
    async function fetchData() {
      if (!professional) return;
      setLoading(true);
      setError(null);
      try {
        // Fetch disponibilites (available slots) using ProfessionalService
        const dispo = await ProfessionalService.getAllDisponibilitiesByProfessionalId(professional.id);
        setDisponibilites(dispo);
        // Fetch consultations (taken rdv)
        const consults = await ProfessionalService.getConsultationsByDoctor(professional.id);
        setConsultations(consults);
        // Fetch profile picture
        if (professional.utilisateur_id) {
          try {
            const res = await UserProfilePicture.getProfilePicture(professional.utilisateur_id);
            setProfilePicture(res?.image_base64 || null);
          } catch {
            setProfilePicture(null);
          }
        } else {
          setProfilePicture(null);
        }
      } catch (err: any) {
        setError('Failed to load availability.');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [professional]);

  // Fetch user data when component mounts
  useEffect(() => {
    const fetchUserData = async () => {
      const email = localStorage.getItem('userEmail');
      if (!email) return; // Not logged in

      setIsLoadingUserData(true);
      try {
        const user = await AuthService.getUserByEmail(email);
        
        const userCountry = user.pays || user.country || 'Tunisia';
        
        const normalizedCountry = normalizeCountry(userCountry);
        
        // Format birthdate to YYYY-MM-DD for date input
        let formattedBirthdate = '';
        const birthdateField = user.date_naissance || user.date_de_naissance;
        if (birthdateField) {
          const dateStr = birthdateField;
          // Handle different date formats
          if (dateStr.includes('T')) {
            // ISO format: 2000-01-15T00:00:00
            formattedBirthdate = dateStr.split('T')[0];
          } else if (dateStr.includes('/')) {
            // Format: DD/MM/YYYY or MM/DD/YYYY
            const parts = dateStr.split('/');
            if (parts.length === 3) {
              // Assume DD/MM/YYYY
              formattedBirthdate = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            }
          } else if (dateStr.includes('-') && dateStr.length === 10) {
            // Already in YYYY-MM-DD format
            formattedBirthdate = dateStr;
          } else {
            formattedBirthdate = dateStr;
          }
        }
        
        setFormData(prev => ({
          ...prev,
          name: (user.prenom ? user.prenom + ' ' : '') + (user.nom || ''),
          email: user.email || '',
          phone: user.telephone || '',
          country: normalizedCountry,
          birthdate: formattedBirthdate,
          address: user.adresse || '',
        }));
        
        console.log('User data fetched:', {
          date_naissance: user.date_naissance,
          date_de_naissance: user.date_de_naissance,
          formattedBirthdate,
          email: user.email,
          fullUserData: user
        });
      } catch (err) {
        console.error('DoctorBooking - Error fetching user data:', err);
        // fallback to empty/defaults if fetch fails
        setFormData(prev => ({
          ...prev,
          name: '',
          email: email,
          phone: '',
          country: 'tunisia',
          birthdate: '',
          address: '',
        }));
      } finally {
        setIsLoadingUserData(false);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  // Helper: get available dates from disponibilites (DisponibilityObj[])
  const getAvailableDates = () => {
    // DisponibilityObj has heure_debut and heure_fin, group by date
    const dateSet = new Set<string>();
    disponibilites.forEach(d => {
      // Extract date from heure_debut (ISO string)
      const dateStr = d.heure_debut.split('T')[0];
      dateSet.add(dateStr);
    });
    return Array.from(dateSet);
  };

  // Check if there's any availability
  const hasAvailability = () => {
    return disponibilites.length > 0;
  };

  // Helper: convert 24-hour time to 12-hour format with lowercase am/pm
  const formatTimeTo12Hour = (time24: string): string => {
    const [hours24, minutes] = time24.split(':').map(Number);
    const period = hours24 >= 12 ? 'pm' : 'am';
    const hours12 = hours24 % 12 || 12;
    return `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Helper: convert 12-hour format back to 24-hour format
  const convertTo24Hour = (time12: string): string => {
    const [time, period] = time12.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    
    let hours24 = hours;
    if (period.toLowerCase() === 'pm' && hours !== 12) {
      hours24 = hours + 12;
    } else if (period.toLowerCase() === 'am' && hours === 12) {
      hours24 = 0;
    }
    
    return `${String(hours24).padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Helper: Adjust appointment time by subtracting 185 minutes for external video API
  // This compensates for the API's 170-minute offset, allowing URL to be valid 15 minutes early
  const adjustTimeForVideoAPI = (dateStr: string, time24: string): { date: string; time: string } => {
    // Combine date and time into a Date object
    const appointmentDateTime = new Date(`${dateStr}T${time24}:00`);
    
    // Subtract 185 minutes (170 for API offset + 15 for early access)
    const adjustedDateTime = new Date(appointmentDateTime.getTime() - (185 * 60 * 1000));
    
    // Format adjusted date as YYYY-MM-DD
    const adjustedDate = adjustedDateTime.toISOString().split('T')[0];
    
    // Format adjusted time as HH:mm:ss
    const adjustedTime = adjustedDateTime.toTimeString().split(' ')[0];
    
    return {
      date: adjustedDate,
      time: adjustedTime
    };
  };

  // Helper: generate time slots every hour between two times (ISO strings)
  function generateHourlySlots(startISO: string, endISO: string): string[] {
    const start = new Date(startISO);
    const end = new Date(endISO);
    const slots: string[] = [];
    let current = new Date(start);
    while (current < end) {
      const time24 = current.toISOString().substring(11, 16); // 'HH:MM'
      slots.push(formatTimeTo12Hour(time24));
      current.setMinutes(current.getMinutes() + 60); // Add 60 minutes (1 hour)
    }
    return slots;
  }

  // Helper: get all possible time slots for a selected date (every hour in disponibilites)
  const getAllTimeSlotsForDate = (date: string) => {
    // For each disponibilite on this date, generate all hour slots
    let slots: string[] = [];
    disponibilites
      .filter(d => d.heure_debut.split('T')[0] === date)
      .forEach(d => {
        slots = slots.concat(generateHourlySlots(d.heure_debut, d.heure_fin));
      });
    // Remove duplicates and sort
    return Array.from(new Set(slots)).sort();
  };

  // Helper: get taken slots for a selected date
  // DB stores consultation time adjusted earlier (user time - 185 minutes). We add 185 to recover displayed user time.
  const getTakenTimeSlotsForDate = (date: string) => {
    return consultations
      .filter(c => c.date === date)
      .map(c => {
        const raw = c.heure.substring(0,5); // HH:MM
        try {
          const dt = new Date(`${date}T${raw}:00`);
            const restored = new Date(dt.getTime() + (185 * 60 * 1000));
          const restoredHours = restored.getHours().toString().padStart(2,'0');
          const restoredMinutes = restored.getMinutes().toString().padStart(2,'0');
          return formatTimeTo12Hour(`${restoredHours}:${restoredMinutes}`);
        } catch (e) {
          return formatTimeTo12Hour(raw);
        }
      });
  };

  // When a date is selected, update all time slots and available slots
  useEffect(() => {
    if (selectedDate) {
      const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
      const allSlots = getAllTimeSlotsForDate(dateStr);
      const takenSlots = getTakenTimeSlotsForDate(dateStr);
      let filteredSlots = allSlots.filter(slot => !takenSlots.includes(slot));

      // Filter out past time slots for today
      const today = new Date();
      const isToday =
        selectedDate.getFullYear() === today.getFullYear() &&
        selectedDate.getMonth() === today.getMonth() &&
        selectedDate.getDate() === today.getDate();
      if (isToday) {
        const currentHour = today.getHours();
        const currentMinute = today.getMinutes();
        filteredSlots = filteredSlots.filter(slot => {
          // Convert 12-hour format to 24-hour for comparison
          const time24 = convertTo24Hour(slot);
          const [hour, minute] = time24.split(":").map(Number);
          return hour > currentHour || (hour === currentHour && minute > currentMinute);
        });
      }
      setAllTimeSlots(allSlots);
      setAvailableTimeSlots(filteredSlots);
      setSelectedTime(''); // Reset time selection on date change
    } else {
      setAllTimeSlots([]);
      setAvailableTimeSlots([]);
    }
  }, [selectedDate, disponibilites, consultations]);

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const isPastDate = (year: number, month: number, date: number): boolean => {
    const today = new Date();
    const compareDate = new Date(year, month, date);
    compareDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  // Update calendar logic to use real available dates
  const isDateAvailable = (year: number, month: number, date: number): boolean => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
    const allSlots = getAllTimeSlotsForDate(dateStr);
    const takenSlots = getTakenTimeSlotsForDate(dateStr);
    // A date is available only if at least one slot is not taken
    return allSlots.some(slot => !takenSlots.includes(slot));
  };

  // Update day labels to start from Monday
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const generateCalendarDays = (year: number, month: number): DateOption[] => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month); // 0 (Sun) - 6 (Sat)
    const days: DateOption[] = [];

    // Calculate the index for Monday as the first day
    // If firstDayOfMonth is 0 (Sunday), shift to 6, else subtract 1
    const startIndex = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

    // Add empty slots for days before the 1st of the month
    for (let i = 0; i < startIndex; i++) {
      days.push({
        day: dayLabels[i],
        date: 0,
        month: month,
        year: year,
        isCurrentMonth: true,
        isPastDate: false,
        isAvailable: false
      });
    }

    // Add days from the current month
    for (let i = 1; i <= daysInMonth; i++) {
      const weekDayIndex = (startIndex + i - 1) % 7;
      days.push({
        day: dayLabels[weekDayIndex],
        date: i,
        month: month,
        year: year,
        isCurrentMonth: true,
        isPastDate: isPastDate(year, month, i),
        isAvailable: isDateAvailable(year, month, i)
      });
    }

    return days;
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateSelect = (dateOption: DateOption) => {
    if (dateOption.date === 0 || !dateOption.isAvailable) return;
    
    const newDate = new Date(dateOption.year, dateOption.month, dateOption.date);
    setSelectedDate(newDate);
    setSelectedTime('');
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
  };

  const formatMonthYear = (date: Date) => {
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const handleInputChange = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };
  const handleCountryChange = (value: string) => {
    setFormData(prev => ({ ...prev, country: value }));
  };
  
  // Video consultation creation removed - now handled automatically by backend webhook after payment
  
  // Stripe success handling (same as Beauty Coaching)
  useEffect(() => {
    console.log('🔍 Payment success handler 1 - checking URL params');
    const sp = new URLSearchParams(window.location.search);
    const type = sp.get('type');
    const status = sp.get('c_payment') || sp.get('payment');
    const sid = sp.get('c_session_id') || sp.get('session_id');
    
    console.log('URL params:', { type, status, sid, paymentFinalized });
    
    if (type !== 'consultation' || status !== 'success' || !sid || paymentFinalized) {
      console.log('Skipping payment handler 1 - conditions not met');
      return;
    }

    const key = `consultation:stripe:sid:${sid}:handled`;
    if (sessionStorage.getItem(key)) {
      console.log('Payment already handled for session:', sid);
      window.history.replaceState(null, '', window.location.pathname);
      return;
    }
    sessionStorage.setItem(key, '1');
    console.log('✅ Processing payment success for session:', sid);

    (async () => {
      try {
        console.log('📞 Fetching consultation data from PaymentService...');
        const data = await PaymentService.getConsultationBySession(sid);
        console.log('💰 Payment data received:', data);
        
        if (data.payment_status === 'paid') {
          if (typeof data.consultation_id === 'number') {
            setConsultationIdForDialog(data.consultation_id);
          }
          
          // Capture video consultation status
          if (data.video_status) {
            setVideoStatusForDialog(data.video_status);
          }
          
          // Video consultation is created automatically by backend webhook
          
          setPaymentFinalized(true);
          setShowSuccessDialog(true);
        }
      } catch (e) {
        console.error('Consultation Stripe success handling failed:', e);
      } finally {
        sp.delete('c_payment');
        sp.delete('payment');
        sp.delete('type');
        sp.delete('c_session_id');
        sp.delete('session_id');
        const newUrl = window.location.origin + window.location.pathname + (sp.toString() ? `?${sp.toString()}` : '');
        window.history.replaceState(null, '', newUrl);
      }
    })();
  }, [paymentFinalized]);

  // Handle Stripe return
  useEffect(() => {
    console.log('🔍 Payment success handler 2 - checking URL params');
    const urlParams = new URLSearchParams(window.location.search);
    const cPayment = urlParams.get('c_payment');
    const type = urlParams.get('type');
    const cSessionId = urlParams.get('c_session_id');

    console.log('URL params (handler 2):', { cPayment, type, cSessionId, paymentFinalized });

    if (cPayment === 'success' && type === 'consultation' && cSessionId && !paymentFinalized) {
      console.log('✅ Processing payment success (handler 2) for session:', cSessionId);
      
      const handlePaymentSuccess = async () => {
        try {
          console.log('📞 Fetching consultation data from PaymentService...');
          const consultationData = await PaymentService.getConsultationBySession(cSessionId);
          console.log('💰 Payment data received:', consultationData);
          
          if (consultationData?.consultation_id) {
            setConsultationIdForDialog(consultationData.consultation_id);
            
            // Capture video consultation status
            if (consultationData.video_status) {
              setVideoStatusForDialog(consultationData.video_status);
            }
            
            // Video consultation is created automatically by backend webhook
            
            setShowSuccessDialog(true);
            setPaymentFinalized(true);
            
            // Clean URL
            const newUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, '', newUrl);
          }
        } catch (err) {
          console.error('Failed to get consultation data:', err);
        }
      };

      handlePaymentSuccess();
    } else {
      console.log('Skipping payment handler 2 - conditions not met');
    }
  }, [paymentFinalized]);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.name.trim()) {
      errors.name = 'Full name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }

    if (!formData.country.trim()) {
      errors.country = 'Country is required';
    }

    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    }

    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!selectedTime || !selectedDate) {
      alert('Please select a time and date');
      return;
    }

    // Format date as YYYY-MM-DD
    const y = selectedDate.getFullYear();
    const m = selectedDate.getMonth() + 1;
    const d = selectedDate.getDate();
    const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

    try {
      setLoading(true);
      setError(null);
      // Request a Stripe checkout session, then redirect to Stripe
      const return_url = `${window.location.origin}/teleconsultation`;
      // Convert selectedTime from 12-hour format (e.g., "09:00 am") to 24-hour format (e.g., "09:00")
      const time24 = convertTo24Hour(selectedTime);
      
      // Adjust the date and time by subtracting 185 minutes to compensate for external video API offset
      const adjusted = adjustTimeForVideoAPI(dateStr, time24);
      
      const session = await PaymentService.createConsultationCheckoutSessionInit({
        professionnel_id: professional!.id,
        date: adjusted.date,        // Send adjusted date
        heure: adjusted.time,        // Send adjusted time (HH:mm:ss format)
        nom_patient: formData.name,
        email: formData.email,
        pays: formData.country,
        telephone: formData.phone,
        sujet: formData.subject,
        return_url,
      });

      if (session?.checkout_url) {
        window.location.href = session.checkout_url;
      } else {
        setError('Failed to create Stripe checkout session');
      }
    } catch (err: any) {
      console.error(err);
      setError('Unable to start payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Do not early-return on missing professional to allow Stripe success handler to run.

  // Language flags mapping using emoji flags or fallback to icon
  const languageFlags: Record<string, React.ReactNode> = {
    'Arabe': <span role="img" aria-label="Tunisia">🇹🇳</span>,
    'Français': <span role="img" aria-label="France">🇫🇷</span>,
    'Anglais': <span role="img" aria-label="UK">🇬🇧</span>,
    'Other': <FlagIcon fontSize="small" style={{ color: '#6a6b7c' }} />
  };

  // Disable submit if any required profile field is missing or subject/date/time missing
  const missingProfile = {
    name: !formData.name?.trim(),
    email: !formData.email?.trim(),
    birthdate: !formData.birthdate?.trim(),
    country: !formData.country?.trim(),
    phone: !formData.phone?.trim(),
    address: !formData.address?.trim(),
  } as const;

  const hasMissingProfile = Object.values(missingProfile).some(Boolean);
  const isSubmitDisabled = hasMissingProfile || !formData.subject.trim() || !selectedDate || !selectedTime || loading;

  return (
    <div className="doctor-booking-root">
      {professional && (
      <div className="doctor-booking-profile-section">
        <div className="doctor-booking-header">
          <div className="back-link" onClick={() => navigate(-1)}>
            &lt; Back to doctor list
          </div>
        </div>
        <div className="doctor-booking-main">
          {/* Top section: Image + Name side by side */}
          <div className="doctor-booking-top-section">
            <div className="doctor-booking-image-section">
              <div className="doctor-booking-image-wrapper">
                {profilePicture ? (
                  <img
                    className="doctor-booking-image"
                    alt={professional.name}
                    src={profilePicture}
                  />
                ) : (
                  <span style={{ display: 'block' }}>
                    <svg width="100%" height="100%" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="36" cy="36" r="36" fill="#e0e7ef" />
                      <circle cx="36" cy="30" r="14" fill="#b0c4de" />
                      <ellipse cx="36" cy="54" rx="20" ry="12" fill="#b0c4de" />
                      <ellipse cx="36" cy="54" rx="16" ry="9" fill="#e0e7ef" />
                      <path d="M36 44c-7 0-14 3-14 7v2c0 1.1.9 2 2 2h24c1.1 0 2-.9 2-2v-2c0-4-7-7-14-7z" fill="#b0c4de" />
                      <circle cx="36" cy="30" r="8" fill="#e0e7ef" />
                      <ellipse cx="36" cy="38" rx="6" ry="4" fill="#e0e7ef" />
                    </svg>
                  </span>
                )}
              </div>
            </div>
            <div className="doctor-booking-name-section">
              <h1 className="doctor-booking-name">{professional.name}</h1>
              <div className="doctor-booking-specialty-container">
                <p className="doctor-booking-specialty">{professional.specialty}</p>
                {/* Specialties tags */}
                <div className="doctor-booking-specialties">
                  {professional.specialties.map((spec, index) => (
                    <span key={index} className="doctor-booking-specialty-tag">{spec}</span>
                  ))}
                </div>
                {/* Meta information below tags */}
                <div className="doctor-booking-meta">
                  <div className="doctor-booking-meta-row">
                    <span className="doctor-booking-meta-label">Languages speaking :</span>
                    {professional.languages.map((lang, index) => (
                      <span key={index} className="doctor-booking-language-item">
                        <span className="doctor-booking-language-flag">{languageFlags[lang] || <FlagIcon fontSize="small" style={{ color: '#6a6b7c' }} />}</span>
                        <span className="doctor-booking-language-name">{lang}</span>
                      </span>
                    ))}
                  </div>
                  <div className="doctor-booking-meta-row">
                    <span className="doctor-booking-meta-label">Available in :</span>
                    <span className="doctor-booking-location-flag">🇹🇳</span>
                    <span className="doctor-booking-location-name">{professional.location}</span>
                  </div>
                  <div className="doctor-booking-meta-row">
                    <span className="doctor-booking-meta-label">Price :</span>
                    <span className="doctor-booking-price">{professional.price}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Description section - separate at the bottom */}
        <div className="doctor-booking-description-section">
          <h3 className="doctor-booking-about-title">About Dr. {professional.name.split(' ').pop()}</h3>
          <p className="doctor-booking-description">{professional.description}</p>
        </div>
      </div>
      )}
      {professional && (
      <div className="doctor-booking-schedule-section">
        <p className="doctor-booking-schedule-label">Schedule</p>
        {loading ? (
          <div className="no-availability-message">
            <h3>Loading Availability...</h3>
            <p>Please wait while we check the doctor's schedule.</p>
          </div>
        ) : !hasAvailability() ? (
          <div className="no-availability-message">
            <h3>No Availability</h3>
            <p>
              Dr. {professional.name.split(' ').pop()} currently has no available time slots.
            </p>
            <p>
              Please check back later or contact the doctor directly.
            </p>
          </div>
        ) : (
          <>
            <div className="doctor-booking-calendar-header">
              <h2 className="doctor-booking-schedule-title">Select prefer Date</h2>
              <div className="doctor-booking-date-nav">
                <button onClick={handlePrevMonth} className="doctor-booking-date-arrow">&lt;</button>
                <span className="doctor-booking-date-month">{formatMonthYear(currentMonth)}</span>
                <button onClick={handleNextMonth} className="doctor-booking-date-arrow">&gt;</button>
              </div>
            </div>
            <div className="doctor-booking-date-section">
              <div className="doctor-booking-date-grid">
                {/* Remove day labels header, show day name in each date circle */}
                {generateCalendarDays(currentMonth.getFullYear(), currentMonth.getMonth()).map((dateOption, index) => (
                  <div
                    key={index}
                    className={`doctor-booking-date-item ${dateOption.date === 0 ? 'empty' : ''} ${!dateOption.isCurrentMonth ? 'other-month' : ''} ${dateOption.isPastDate ? 'past-date' : ''
                      } ${!dateOption.isAvailable ? 'unavailable' : ''
                      } ${selectedDate?.getDate() === dateOption.date &&
                        selectedDate?.getMonth() === dateOption.month &&
                        selectedDate?.getFullYear() === dateOption.year ? 'selected' : ''
                      }`}
                    onClick={() => dateOption.date !== 0 && handleDateSelect(dateOption)}
                  >
                    {dateOption.date !== 0 && (
                      <>
                        <span className="doctor-booking-date-day">{dateOption.day}</span>
                        <span className="doctor-booking-date-number">{dateOption.date}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="doctor-booking-time-section">
              <h3 className="doctor-booking-time-title">Select prefer Time</h3>
              <div className="doctor-booking-time-grid">
                {allTimeSlots.map((time, index) => {
                  const isAvailable = availableTimeSlots.includes(time);
                  return (
                    <div
                      key={index}
                      className={`doctor-booking-time-item${selectedTime === time ? ' selected' : ''}${!isAvailable ? ' disabled' : ''}`}
                      onClick={() => isAvailable && handleTimeSelect(time)}
                      style={{ pointerEvents: isAvailable ? 'auto' : 'none', opacity: isAvailable ? 1 : 0.5 }}
                    >
                      <span className="doctor-booking-time-value">{time}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
      )}
      {professional && hasAvailability() && !loading && (
      <div className="doctor-booking-form-section" style={{ width: '100%' }}>
        <h2 className="doctor-booking-form-title">Consultation informations</h2>
        {isLoadingUserData ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>Loading your information...</p>
          </div>
        ) : (
        <div className="doctor-booking-form-container">
          <div className="doctor-booking-form-row">
            <div className="doctor-booking-field-wrapper">
              <InputField
                placeholder="Name & Surname"
                value={formData.name}
                onChange={handleInputChange('name')}
                required
                disabled
              />
                {formErrors.name && <p className="error-message">{formErrors.name}</p>}
                {missingProfile.name && (
                  <p className="error-message">Please complete this information in your profile</p>
                )}
            </div>
            <div className="doctor-booking-field-wrapper">
              <InputField
                placeholder="E-mail address"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                required
                disabled
              />
                {formErrors.email && <p className="error-message">{formErrors.email}</p>}
                {missingProfile.email && (
                  <p className="error-message">Please complete this information in your profile</p>
                )}
            </div>
          </div>
          <div className="doctor-booking-form-row">
            <div className="doctor-booking-field-wrapper">
              <InputField
                placeholder="Birthdate"
                type="date"
                value={formData.birthdate}
                onChange={handleInputChange('birthdate')}
                required
                disabled
              />
                {formErrors.birthdate && <p className="error-message">{formErrors.birthdate}</p>}
                {missingProfile.birthdate && (
                  <p className="error-message">Please complete this information in your profile</p>
                )}
            </div>
            <div className="doctor-booking-field-wrapper">
              <Dropdown
                placeholder="Country"
                options={countryOptions}
                value={formData.country}
                onChange={handleCountryChange}
                disabled
              />
                {formErrors.country && <p className="error-message">{formErrors.country}</p>}
                {missingProfile.country && (
                  <p className="error-message">Please complete this information in your profile</p>
                )}
            </div>
          </div>
          <div className="doctor-booking-form-row">
            <div className="doctor-booking-field-wrapper">
              <PhoneNumberField
                useCForm={false}
                required
                value={formData.phone}
                onChange={(value) => setFormData((prev) => ({ ...prev, phone: value }))}
                defaultCountryCode="+216"
                  isDisabled
                disableGridWrapper
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '1.25rem',
                    height: '48px',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#C37559',
                    },
                  },
                  '& label.Mui-focused': {
                    color: '#C37559',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#C37559',
                  },
                  width: '100%'
                }}
              />
                {formErrors.phone && <p className="error-message">{formErrors.phone}</p>}
                {missingProfile.phone && (
                  <p className="error-message">Please complete this information in your profile</p>
                )}
            </div>
            <div className="doctor-booking-field-wrapper">
              <InputField
                placeholder="Address"
                value={formData.address}
                onChange={handleInputChange('address')}
                required
                  disabled
              />
                {formErrors.address && <p className="error-message">{formErrors.address}</p>}
                {missingProfile.address && (
                  <p className="error-message">Please complete this information in your profile</p>
                )}
            </div>
          </div>
          <div className="doctor-booking-form-row">
            <div className="doctor-booking-field-wrapper" style={{ width: '100%' }}>
              <Textarea
                placeholder="Subject..."
                value={formData.subject}
                onChange={handleInputChange('subject')}
                rows={6}
              />
              {formErrors.subject && <p className="error-message">{formErrors.subject}</p>}
            </div>
          </div>
          <div className="doctor-booking-form-submit">
            <button
              onClick={handleFormSubmit}
              className="doctor-booking-button doctor-booking-button-primary doctor-booking-button-large"
                disabled={isSubmitDisabled}
                aria-disabled={isSubmitDisabled}
                tabIndex={isSubmitDisabled ? -1 : 0}
            >
              Confirm & Pay
            </button>
          </div>
        </div>
        )}
      </div>
      )}
      <PaymentSuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
      />
    </div>
  );
};

export default DoctorBooking;